import { deleteField } from "firebase/firestore";
import { t } from "i18next";
import { useEffect, useState } from "react";

import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { Checkbox } from "@src/components/ui-components/Checkbox/Checkbox";
import { Popup } from "../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../components/deprecated/PopupTemplates/PopupTemplates";
import { PropertySelector } from "../../../components/deprecated/PropertySelector/PropertySelector";
import { ReadAccessPopup } from "../../../components/deprecated/ReadAccessPopup/ReadAccessPopup";
import { NewRightFullHeightPopup } from "../../../components/deprecated/RightFullHeight/RightFullHeightPopUp";
import { RoleSelectionField } from "../../../components/deprecated/RoleSelectionField/RoleSelectionField";
import { Text } from "../../../components/deprecated/Text/Text";
import { TextInputField } from "../../../components/deprecated/TextInputField/TextInputField";
import { SectionSplitterLabel } from "../../../components/ui-components/SectionSplitterLabel/SectionSplitterLabel";
import { ToastUtilities } from "../../../libs/react-toastify/ToastUtilities";
import { CustomFieldDataType } from "../../../to-be-removed/LocalData/Company/Collections/CustomFieldDataType";
import { ReadAccess } from "../../../to-be-removed/LocalData/General/AccessData";
import { LocalData } from "../../../to-be-removed/LocalData/General/LocalData";
import { V2CollectionItemData } from "../../../to-be-removed/LocalData/General/V2CollectionItemData";
import { PropertyData } from "../../../to-be-removed/LocalData/Property/PropertyData";
import { Role } from "../../../to-be-removed/LocalData/User/Role";
import { CollectionData } from "../../../to-be-removed/LocalData/collections/CollectionData";
import { CollectionFieldData } from "../../../to-be-removed/LocalData/collections/CollectionFieldData";
import { loadAllGlobalDataTypes } from "../../../utils/hooks/useGlobalDataTypes";
import { SystemsAndComponentsUtilities } from "../../../utils/systemsAndComponentsUtilities";
import { Utilities } from "../../../utils/utilities";
import { useCollectionFields } from "../hooks/useCollectionFields";
import styles from "./V2CollectionItemSettings.module.css";
import { AriaButton } from "@src/components/ui-components/Buttons/AriaButton/AriaButton";
import { Delete } from "@icons/untitled_ui_icons";

interface CollectionItemSettingsProps {
  id: string;
  item?: V2CollectionItemData;
  collection: CollectionData;
  didSave: (item: V2CollectionItemData) => void;
  defaultProperties?: PropertyData[];
}

type CollectionItemSettingsField = {
  field: CollectionFieldData;
  value: any;
};

export const V2CollectionItemSettings = (props: CollectionItemSettingsProps) => {
  const collectionFields = useCollectionFields(props.collection);
  const [, setLastUpdate] = useState(new Date());
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(props.item?.name ?? "");
  const [roles, setRoles] = useState<Role[]>(props.item?.roleIDs.map((r) => Role.getRoleWithId(r)) ?? [Role.LANDLORD]);
  const [hasCustomName, setHasCustomName] = useState<boolean>((props.item?.name.length ?? 0) > 0);
  const [fields, setFields] = useState<CollectionItemSettingsField[]>([]);
  const [properties, setProperties] = useState<PropertyData[]>(props.item?.properties ?? props.defaultProperties ?? []);
  const [readAccess, setReadAccess] = useState<ReadAccess>(props.item?.readAccess ?? { users: [LocalData.currentUser] });
  const [writeAccess, setWriteAccess] = useState<ReadAccess>({});
  const [useCustomWriteAccess, setUseCustomWriteAccess] = useState(props.item?.writeAccess !== undefined);
  const [changedAccess, setChangedAccess] = useState(false);

  const nsFields = fields.filter((field) => field.field.dataType === CustomFieldDataType.NORWEGIAN_STANDARDS);
  const otherFields = fields.filter((field) => !nsFields.includes(field));

  const canSave = !saving && name.trim().length > 0 && properties.length > 0;

  function updateFields() {
    const fields: CollectionItemSettingsField[] = [];
    for (const doc of collectionFields.filter((field) => CustomFieldDataType.FIELD_TYPES.includes(field.dataType))) {
      fields.push({
        field: doc,
        value: props.item?.fields[doc.fieldID]?.value ?? undefined,
      });
    }

    setFields(fields);
    for (const field of collectionFields) {
      if (field.dataType === CustomFieldDataType.CUSTOM_DATA_TYPE) {
        loadAllGlobalDataTypes().then(() => setLastUpdate(new Date()));
      }
    }
  }

  useEffect(() => {
    updateFields();
  }, [collectionFields.length]);

  function saveTapped() {
    setSaving(true);

    const newReadAccess = readAccess;
    if (props.item?.readAccess.propertyRoles) {
      newReadAccess.propertyRoles = props.item.readAccess.propertyRoles;
    }

    const saveData: { [key: string]: any } = {
      name: name.trim(),
      readAccess: Utilities.convertReadAccess(newReadAccess),
      propertyIDs: properties.map((p) => p.id),
      roleIDs: roles.map((r) => r.id),
    };

    const fieldData: { [key: string]: any } = {};
    for (const field of fields) {
      if (field.value !== undefined && field.value !== null) {
        if (props.item) {
          saveData["fields." + field.field.fieldID] = { value: field.value };
        } else {
          fieldData[field.field.fieldID] = { value: field.value };
        }
      }
    }

    if (props.item) {
      saveData["writeAccess"] = useCustomWriteAccess ? Utilities.convertReadAccess(writeAccess ?? {}) : deleteField();
      ToastUtilities.presentSavingToast(
        props.item
          .saveUpdate(saveData)
          .then((item) => {
            props.didSave(item);
            Popup.remove(props.id);
          })
          .catch(() => {
            ToastUtilities.presentError(t("main.errorOccured"));
            setSaving(false);
          })
      );
    } else if (props.collection) {
      saveData["collectionID"] = props.collection.id;
      saveData["fields"] = fieldData;
      if (useCustomWriteAccess) {
        saveData["writeAccess"] = Utilities.convertReadAccess(writeAccess ?? {});
      }

      ToastUtilities.presentSavingToast(
        LocalData.collectionItems
          .createData(saveData)
          .then((item) => {
            props.didSave(item);
            Popup.remove(props.id);
          })
          .catch(() => {
            ToastUtilities.presentError(t("main.errorOccured"));
            setSaving(false);
          })
      );
    }
  }

  function updateValueInField(index: number, value: any) {
    const newFields = [...fields];
    newFields[index].value = value;
    setFields(newFields);
  }

  function editReadAccess() {
    Popup.present((id) => (
      <ReadAccessPopup
        id={id}
        accessGroups={["companies", "contractorGroups", "users"]}
        requiredAccess={props.collection?.requiredWriteAccess}
        disableCurrentUserAccessCheck={true}
        context={{}}
        onSelect={(access) => {
          setReadAccess(access);
          if (!useCustomWriteAccess) setWriteAccess(access);
          setChangedAccess(true);
        }}
        readAccess={readAccess}
      />
    ));
  }

  function editWriteAccess() {
    Popup.present((id) => (
      <ReadAccessPopup
        id={id}
        accessGroups={["roles", "contractorGroups", "users"]}
        requiredAccess={props.collection?.requiredWriteAccess}
        disableCurrentUserAccessCheck={true}
        context={{}}
        onSelect={(access) => {
          setWriteAccess(access);
          setChangedAccess(true);
        }}
        readAccess={readAccess}
      />
    ));
  }
  function deleteTapped() {
    if (props.item) {
      PopupTemplates.presentVerifyDelete(props.item.name, () => {
        Popup.remove(props.id);
        ToastUtilities.presentPromise(t("main.deleting"), LocalData.collectionItems.deleteDocumentWithDeletedBy(props.item!.id));
      });
    }
  }

  return (
    <NewRightFullHeightPopup
      id={props.id}
      children={
        <div className={styles.container}>
          <Text
            size="xl"
            isBold
            text={props.item ? t("main.settings") : t("main.create") + " " + props.collection?.name.translate(1).toLowerCase()}
          />

          <ul id={styles.fields}>
            {nsFields.map((field) => {
              return (
                <li key={field.field.fieldID}>
                  <Text
                    className={styles.fieldTitle}
                    size="sm"
                    text={field.field.name.translate() + (field.field.deprecated ? " (" + t("main.deprecated") + ")" : "")}
                  />
                  {field.field.dataType.getDataEditor(
                    field.value,
                    field.field.dataOptions,
                    { propertyID: properties.length > 0 ? properties[0].id : undefined },
                    (data) => {
                      const index = fields.findIndex((f) => f.field.fieldID === field.field.fieldID);
                      updateValueInField(index, data);
                      if (field.field.dataType === CustomFieldDataType.NORWEGIAN_STANDARDS && data && (!hasCustomName || name.trim().length === 0)) {
                        setName(SystemsAndComponentsUtilities.getOptionWithKey(undefined, data)?.title ?? "");
                      }
                    }
                  )}
                </li>
              );
            })}
            <li key="name">
              <Text className={styles.fieldTitle} size="sm" text={t("main.name") + "*"} />
              <TextInputField
                placeholder={t("main.enterName")}
                fullWidth
                value={name}
                onChange={(text) => {
                  setName(text);
                  setHasCustomName(true);
                }}
              />
            </li>
            <li key="properties">
              <Text className={styles.fieldTitle} size="sm" text={t("main.properties") + "*"} />
              <PropertySelector
                isMulti
                optionsSelected={properties}
                properties={LocalData.currentUser.properties}
                changedSelectedProperties={setProperties}
                width="100%"
              />
            </li>
            <li key="roles">
              <Text className={styles.fieldTitle} size="sm" text={t("main.collectionItemSettings.roles")} />
              <RoleSelectionField value={roles} onChange={setRoles} />
            </li>
            <SectionSplitterLabel text={t("main.settings")} />
            {otherFields.map((field) => {
              return (
                <li key={field.field.fieldID}>
                  <Text
                    className={styles.fieldTitle}
                    size="sm"
                    text={field.field.name.translate() + (field.field.deprecated ? " (" + t("main.deprecated") + ")" : "")}
                  />
                  {field.field.dataType.getDataEditor(
                    field.value,
                    field.field.dataOptions,
                    { propertyID: properties.length > 0 ? properties[0].id : undefined },
                    (data) => {
                      const index = fields.findIndex((f) => f.field.fieldID === field.field.fieldID);
                      updateValueInField(index, data);
                    }
                  )}
                </li>
              );
            })}

            <SectionSplitterLabel text={t("main.access")} />
            <li key="readAccess">
              <Text size="sm" text={t("main.collectionItemSettings.readAccess")} />
              <Text size="sm" className={styles.fieldTitleGray} text={t("main.collectionItemSettings.readAccessTooltip")} />
              <AriaButton variant="secondary" style={{ marginTop: 8 }} onPress={() => editReadAccess()}>
                {Utilities.getReadAccessString(readAccess, { excludePropertyRoles: true })}
              </AriaButton>
            </li>

            <li key="writeAccess">
              <Text size="sm" className={styles.fieldTitle} text={t("main.writeAccess")} />
              <Checkbox
                value={!useCustomWriteAccess}
                description={t("main.useCustomWriteAccess")}
                onChange={() => {
                  setChangedAccess(true);
                  setUseCustomWriteAccess(!useCustomWriteAccess);
                }}
              />
              {useCustomWriteAccess && (
                <AriaButton variant="secondary" onPress={() => editWriteAccess()}>
                  {Utilities.getReadAccessString(writeAccess)}
                </AriaButton>
              )}
            </li>
          </ul>

          <div>
            <Button
              state={saving ? "loading" : canSave ? "default" : "disabled"}
              onClick={() => saveTapped()}
              title={props.item ? t("main.save") : t("main.create")}
              trailingIcon="save"
              fullWidth
            />
            {props.item && (
              <AriaButton style={{ marginTop: 8 }} color="destructive" fullWidth onPress={() => deleteTapped()}>
                <Delete />
                {t("main.delete")}
              </AriaButton>
            )}
          </div>
        </div>
      }
    />
  );
};
